// components
import { Divider, Typography } from '@mui/material';
import { Icon } from '@sdb/planning-ui';
import { StyledBoxFlexCenter } from '../Box/Box.styles';
import {
  StyledCustomRichTextSaveButton,
  StyledEditorContentContainer,
  StyledRTEButton,
  StyledToolsBox,
  StyledWordCountContainer
} from './RichTextEditor.styles';

// hooks
import {
  EditorContent,
  useEditor,
  useEditorState,
  EditorContext
} from '@tiptap/react';

// types
import type { Editor } from '@tiptap/react';

// utils
import { t } from 'i18next';
import { Color } from '@tiptap/extension-text-style';
import { CharacterCount } from '@tiptap/extensions';
import { Placeholder } from '@tiptap/extensions';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { TableKit } from '@tiptap/extension-table';
import { useEffect } from 'react';

const extensions = [
  TextStyle,
  Color,
  StarterKit,
  TextAlign.configure({
    types: ['heading', 'paragraph']
  }),
  Placeholder.configure({
    placeholder: 'Start typing...'
  }),
  TableKit.configure({
    table: { resizable: false }
  })
];

const MenuBar = ({
  editor,
  textLimit,
  onSave
}: {
  editor: Editor;
  textLimit?: number;
  onSave?: {
    label?: string;
    action: () => void;
    isLoading?: boolean;
  };
}) => {
  const editorState = useEditorState({
    editor,
    selector: (ctx) => {
      return {
        canUndo: ctx.editor.can().chain().undo().run() ?? false,
        canRedo: ctx.editor.can().chain().redo().run() ?? false,
        isBold: ctx.editor.isActive('bold') ?? false,
        canBold: ctx.editor.can().chain().toggleBold().run() ?? false,
        isItalic: ctx.editor.isActive('italic') ?? false,
        canItalic: ctx.editor.can().chain().toggleItalic().run() ?? false,
        isStrike: ctx.editor.isActive('strike') ?? false,
        isUnderline: ctx.editor.isActive('underline') ?? false,
        canStrike: ctx.editor.can().chain().toggleStrike().run() ?? false,
        canUnderline: ctx.editor.can().chain().toggleUnderline().run() ?? false,
        isCode: ctx.editor.isActive('code') ?? false,
        canCode: ctx.editor.can().chain().toggleCode().run() ?? false,
        canClearMarks: ctx.editor.can().chain().unsetAllMarks().run() ?? false,
        isParagraph: ctx.editor.isActive('paragraph') ?? false,
        color: ctx.editor.getAttributes('textStyle').color,
        isHeading1: ctx.editor.isActive('heading', { level: 1 }) ?? false,
        isHeading2: ctx.editor.isActive('heading', { level: 2 }) ?? false,
        isHeading3: ctx.editor.isActive('heading', { level: 3 }) ?? false,
        isHeading4: ctx.editor.isActive('heading', { level: 4 }) ?? false,
        isHeading5: ctx.editor.isActive('heading', { level: 5 }) ?? false,
        isHeading6: ctx.editor.isActive('heading', { level: 6 }) ?? false,
        isBulletList: ctx.editor.isActive('bulletList') ?? false,
        isOrderedList: ctx.editor.isActive('orderedList') ?? false,
        isCodeBlock: ctx.editor.isActive('codeBlock') ?? false,
        isBlockquote: ctx.editor.isActive('blockquote') ?? false,
        isAlignLeft: ctx.editor.isActive({ textAlign: 'left' }) ?? false,
        isAlignCenter: ctx.editor.isActive({ textAlign: 'center' }) ?? false,
        isAlignRight: ctx.editor.isActive({ textAlign: 'right' }) ?? false,
        charactersCount: ctx.editor.storage.characterCount.characters(),
        wordsCount: ctx.editor.storage.characterCount.words()
      };
    }
  });

  const percentage =
    editor && textLimit
      ? Math.round((100 / textLimit) * editorState.charactersCount)
      : 0;

  return (
    <>
      <StyledToolsBox>
        <StyledRTEButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editorState.canUndo}
        >
          <Icon type="arrow-rotate-left" />
        </StyledRTEButton>
        <StyledRTEButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editorState.canRedo}
        >
          <Icon type="arrow-rotate-right" />
        </StyledRTEButton>

        <Divider orientation="vertical" variant="middle" flexItem />

        <StyledRTEButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editorState.canBold}
          className={editorState.isBold ? 'is-active' : ''}
        >
          <Icon type="bold" />
        </StyledRTEButton>

        <StyledRTEButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editorState.canItalic}
          className={editorState.isItalic ? 'is-active' : ''}
        >
          <Icon type="italic" />
        </StyledRTEButton>
        <StyledRTEButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          disabled={!editorState.canUnderline}
          className={editorState.isUnderline ? 'is-active' : ''}
        >
          <Icon type="underline" />
        </StyledRTEButton>
        <StyledRTEButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          disabled={!editorState.canStrike}
          className={editorState.isStrike ? 'is-active' : ''}
        >
          <Icon type="strikethrough" />
        </StyledRTEButton>

        <Divider orientation="vertical" variant="middle" flexItem />

        <input
          type="color"
          onInput={(event) =>
            editor.chain().focus().setColor(event.currentTarget.value).run()
          }
          value={editorState.color}
          data-testid="setColor"
          style={{ width: '32px' }}
        />
        <Divider orientation="vertical" variant="middle" flexItem />

        <StyledRTEButton
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className={editorState.isAlignLeft ? 'is-active' : ''}
        >
          <Icon type="align-left" />
        </StyledRTEButton>

        <StyledRTEButton
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className={editorState.isAlignCenter ? 'is-active' : ''}
        >
          <Icon type="align-center" />
        </StyledRTEButton>

        <StyledRTEButton
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          className={editorState.isAlignRight ? 'is-active' : ''}
        >
          <Icon type="align-right" />
        </StyledRTEButton>
        <Divider orientation="vertical" variant="middle" flexItem />

        <StyledRTEButton
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          }
          className={editorState.isHeading1 ? 'is-active' : ''}
        >
          H1
        </StyledRTEButton>
        <StyledRTEButton
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
          className={editorState.isHeading2 ? 'is-active' : ''}
        >
          H2
        </StyledRTEButton>
        <StyledRTEButton
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
          className={editorState.isHeading3 ? 'is-active' : ''}
        >
          H3
        </StyledRTEButton>
        <StyledRTEButton
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 4 }).run()
          }
          className={editorState.isHeading4 ? 'is-active' : ''}
        >
          H4
        </StyledRTEButton>
        <StyledRTEButton
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 5 }).run()
          }
          className={editorState.isHeading5 ? 'is-active' : ''}
        >
          H5
        </StyledRTEButton>
        <StyledRTEButton
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 6 }).run()
          }
          className={editorState.isHeading6 ? 'is-active' : ''}
        >
          H6
        </StyledRTEButton>

        <Divider orientation="vertical" variant="middle" flexItem />

        <StyledRTEButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editorState.isBulletList ? 'is-active' : ''}
        >
          <Icon type="list-ul" />
        </StyledRTEButton>
        <StyledRTEButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={editorState.isOrderedList ? 'is-active' : ''}
        >
          <Icon type="list-ol" />
        </StyledRTEButton>
        <StyledRTEButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className={editorState.isBlockquote ? 'is-active' : ''}
        >
          <Icon type="quote-right" />
        </StyledRTEButton>

        {onSave && (
          <StyledCustomRichTextSaveButton
            onClick={onSave.action}
            variant="contained"
            loading={onSave.isLoading}
          >
            <Icon type="paper-plane" />
            {onSave?.label ? onSave.label : t('save')}
          </StyledCustomRichTextSaveButton>
        )}
      </StyledToolsBox>

      {!!textLimit && (
        <StyledWordCountContainer
          className={`character-count ${
            editorState.charactersCount === textLimit
              ? 'character-count--warning'
              : ''
          }`}
        >
          <StyledBoxFlexCenter>
            <svg height="20" width="20" viewBox="0 0 20 20">
              <circle r="10" cx="10" cy="10" fill="#e9ecef" />
              <circle
                r="5"
                cx="10"
                cy="10"
                fill="transparent"
                stroke="currentColor"
                strokeWidth="10"
                strokeDasharray={`calc(${percentage} * 31.4 / 100) 31.4`}
                transform="rotate(-90) translate(-20)"
              />
              <circle r="6" cx="10" cy="10" fill="white" />
            </svg>
            <Typography variant="body2" color="textSecondary">
              {editorState.charactersCount} / {textLimit} characters
            </Typography>
          </StyledBoxFlexCenter>
          <Typography variant="body2" color="textSecondary">
            {editorState.wordsCount} words
          </Typography>
        </StyledWordCountContainer>
      )}
    </>
  );
};

type RichTextEditorProps = {
  value?: string; // <-- controlled value (HTML)
  onChange?: (html: string) => void; // <-- notify RHF
  onBlur?: () => void; // <-- notify RHF
  textLimit?: number;
  onSave?: { label?: string; action: () => void; isLoading?: boolean };
  /** Kept for backwards compat; used as initial value if `value` is undefined */
  content?: string;
};

export default function RichTextEditor({
  value,
  onChange,
  onBlur,
  textLimit,
  onSave,
  content
}: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      ...extensions,
      CharacterCount.configure({
        limit: textLimit
      })
    ],
    content: value ?? content ?? '',
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
    onBlur: () => {
      onBlur?.();
    }
  });

  useEffect(() => {
    if (!editor) return;
    const next = value ?? content ?? '';
    const curr = editor.getHTML();
    if (next !== curr) {
      editor.commands.setContent(next, { emitUpdate: false });
    }
  }, [value, content, editor]);

  if (!editor) return null;

  return (
    <EditorContext.Provider value={{ editor }}>
      <MenuBar editor={editor} textLimit={textLimit} onSave={onSave} />
      <StyledEditorContentContainer>
        <EditorContent editor={editor} />
      </StyledEditorContentContainer>
    </EditorContext.Provider>
  );
}
